/**
 * Retention for the browser's offline store.
 *
 * IndexedDB entries are kept until they have SYNCED and then aged past the
 * retention window, at which point they are purged. Without this the store grows
 * for the life of the browser profile: a therapist's tablet accumulates every
 * datasheet they have ever filled in, and the Sync Status page becomes unusable.
 *
 * THE ONE RULE: UNSYNCED WORK IS NEVER DELETED. Not when it is old, not when the
 * window is small, not when it has failed to sync a hundred times. An unsynced
 * entry is the ONLY copy of clinical data somebody recorded, possibly in a room
 * with no signal, and no cleanup job gets to decide it is stale. Everything here
 * filters on synced === true / syncStatus === 'synced' before deleting anything.
 *
 * WHERE THE WINDOW COMES FROM, in order:
 *   1. GET /settings/client   the backend's OFFLINE_RETENTION_DAYS. Changeable
 *                             with a Helm values edit and a pod restart.
 *   2. VITE_OFFLINE_RETENTION_DAYS   baked in at build time; a fallback for when
 *                             the endpoint cannot be reached (offline at start).
 *   3. DEFAULT_RETENTION_DAYS
 *
 * The backend is first deliberately. A `VITE_*` value is compiled into the
 * bundle, so changing it needs a rebuild and a new image - the wrong shape for
 * something an operator may want to change after an incident.
 */
import { get } from 'services/api/client';
import { clearSyncedEntriesForTherapist, clearSyncedSessionUpdatesForTherapist } from 'utils/offlineDB';

export const DEFAULT_RETENTION_DAYS = 7;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** Remember the last run so a purge happens at most once a day per browser. */
const LAST_RUN_KEY = 'ayro.offlineRetention.lastRun';

/** Cached for the tab's lifetime; the window does not change mid-session. */
let cachedDays = null;

/**
 * The retention window in days.
 *
 * A value of 0 or less disables retention entirely - nothing is ever purged -
 * which is the safe reading of "unset", not "delete everything immediately".
 */
export const getRetentionDays = async () => {
  if (cachedDays !== null) return cachedDays;

  const buildTime = Number(import.meta.env.VITE_OFFLINE_RETENTION_DAYS);
  const fallback = Number.isFinite(buildTime) && buildTime > 0 ? buildTime : DEFAULT_RETENTION_DAYS;

  try {
    const res = await get('/settings/client');
    const days = Number(res?.offline_retention_days);
    cachedDays = Number.isFinite(days) ? days : fallback;
  } catch {
    // Offline, or the endpoint is not deployed yet. Fall back rather than
    // skipping retention altogether, but never invent a shorter window.
    cachedDays = fallback;
  }
  return cachedDays;
};

/**
 * Purge synced entries older than the retention window.
 *
 * @param {string} userId whose entries to purge - the store is shared by every
 *   account that has signed in on this browser, and one user's cleanup must not
 *   touch another's queue.
 * @param {{force?: boolean}} [opts] force skips the once-a-day throttle.
 * @returns {Promise<{skipped?: string, days?: number, entries?: number, sessionUpdates?: number}>}
 */
export const purgeExpiredOfflineData = async (userId, { force = false } = {}) => {
  if (!userId) return { skipped: 'no-user' };

  if (!force) {
    try {
      const last = Number(window.localStorage.getItem(LAST_RUN_KEY));
      if (Number.isFinite(last) && Date.now() - last < MS_PER_DAY) {
        return { skipped: 'ran-today' };
      }
    } catch {
      // Private mode or blocked storage: fall through and just run it.
    }
  }

  const days = await getRetentionDays();
  if (!days || days <= 0) return { skipped: 'disabled', days };

  const olderThanMs = days * MS_PER_DAY;
  let entries = 0;
  let sessionUpdates = 0;
  try {
    // Both helpers already filter to synced-only; the window is all that is
    // being supplied here.
    entries = (await clearSyncedEntriesForTherapist(userId, { olderThanMs })) || 0;
    sessionUpdates = (await clearSyncedSessionUpdatesForTherapist(userId, { olderThanMs })) || 0;
  } catch (err) {
    console.warn('[OfflineRetention] Purge failed:', err);
    return { skipped: 'error', days };
  }

  try {
    window.localStorage.setItem(LAST_RUN_KEY, String(Date.now()));
  } catch {
    /* not fatal - the throttle is a nicety, the purge already happened */
  }

  if (entries || sessionUpdates) {
    console.warn(
      `[OfflineRetention] Purged ${entries} synced entr${entries === 1 ? 'y' : 'ies'} and ` +
        `${sessionUpdates} session update(s) older than ${days} day(s).`
    );
  }
  return { days, entries, sessionUpdates };
};

export default purgeExpiredOfflineData;
